import React, { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

export default function ImageModal({ open, onClose, images = [], title, startIndex = 0 }) {
  const [idx, setIdx] = useState(startIndex);
  useEffect(() => { if (open) setIdx(startIndex); }, [open, startIndex]);

  const prev = useCallback(() => setIdx((i) => (i - 1 + images.length) % images.length), [images.length]);
  const next = useCallback(() => setIdx((i) => (i + 1) % images.length), [images.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, prev, next, onClose]);

  if (!images.length) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="image-modal"
        className="max-w-[100vw] w-[100vw] h-[100vh] sm:max-w-[100vw] p-0 bg-[#0C0C0C] border-0 rounded-none flex items-center justify-center overflow-hidden translate-x-[-50%] translate-y-[-50%]">
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <button
          onClick={onClose}
          aria-label="Close"
          data-testid="image-modal-close"
          className="absolute top-4 right-4 z-10 w-11 h-11 border border-[#2A2A2A] text-[#F5F1EB] hover:border-[#C9A55A] hover:text-[#C9A55A] flex items-center justify-center bg-[#0C0C0C]/70">
          <X size={18} />
        </button>

        <div className="absolute top-5 left-6 text-[#C9A55A] text-overline z-10">{title}</div>
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-[#F5F1EB] text-xs tracking-[0.18em] z-10">
          {idx + 1} / {images.length}
        </div>

        <div className="relative w-full h-full flex items-center justify-center">
          <img src={images[idx]} alt={`${title} ${idx + 1}`} className="max-w-full max-h-full object-contain" />
        </div>

        {images.length > 1 && (
          <>
            <button onClick={prev} aria-label="Previous"
              data-testid="image-modal-prev"
              className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-12 h-12 border border-[#2A2A2A] text-[#F5F1EB] hover:border-[#C9A55A] hover:text-[#C9A55A] flex items-center justify-center bg-[#0C0C0C]/60">
              <ChevronLeft size={20} />
            </button>
            <button onClick={next} aria-label="Next"
              data-testid="image-modal-next"
              className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-12 h-12 border border-[#2A2A2A] text-[#F5F1EB] hover:border-[#C9A55A] hover:text-[#C9A55A] flex items-center justify-center bg-[#0C0C0C]/60">
              <ChevronRight size={20} />
            </button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
